/**
 * Google Analytics 服务
 * 使用官方 gtag.js 实现
 */

import { GA_MEASUREMENT_ID, isGAConfigured, GA_CONFIG, GA_EVENTS, GAEventParams } from '../config/analytics';
import { log } from '../utils/logger';

// 声明全局 gtag 函数
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

class AnalyticsService {
  private initialized = false;
  private scriptLoaded = false;

  /**
   * 初始化 Google Analytics
   */
  async initialize(): Promise<void> {
    // 生产环境检查
    const isProduction = import.meta.env.PROD;
    
    // 如果未配置或在开发环境且未明确启用，则跳过
    if (!isGAConfigured) {
      log.warn('Google Analytics 未配置，跳过初始化');
      return;
    }

    // 开发环境可选择性启用
    if (!isProduction && !import.meta.env.VITE_ENABLE_GA_IN_DEV) {
      log.info('开发环境未启用 Google Analytics');
      return;
    }

    try {
      await this.loadGAScript();
      this.initializeGA();
      this.initialized = true;
      log.info('Google Analytics 初始化成功', { measurementId: GA_MEASUREMENT_ID });
    } catch (error) {
      log.error('Google Analytics 初始化失败:', error);
    }
  }

  /**
   * 加载 GA 脚本
   */
  private async loadGAScript(): Promise<void> {
    if (this.scriptLoaded) return;

    return new Promise((resolve, reject) => {
      // 创建 gtag 脚本
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Analytics script'));
      };

      // 添加到页面
      document.head.appendChild(script);
    });
  }

  /**
   * 初始化 GA 配置
   */
  private initializeGA(): void {
    // 初始化 dataLayer
    window.dataLayer = window.dataLayer || [];
    
    // 定义 gtag 函数
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };

    // 配置 GA
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, GA_CONFIG);
  }

  /**
   * 发送页面浏览事件
   */
  trackPageView(path?: string, title?: string): void {
    if (!this.initialized) return;

    const params: GAEventParams = {};
    
    if (path) {
      params.page_location = window.location.origin + path;
      params.page_path = path;
    }
    
    if (title) {
      params.page_title = title;
    }

    window.gtag('event', GA_EVENTS.PAGE_VIEW, params);
    log.debug('GA: 页面浏览事件已发送', { path, title });
  }

  /**
   * 发送自定义事件
   */
  trackEvent(eventName: string, params: GAEventParams = {}): void {
    if (!this.initialized) return;

    window.gtag('event', eventName, params);
    log.debug('GA: 自定义事件已发送', { eventName, params });
  }

  /**
   * 用户登录事件
   */
  trackLogin(method: string = 'email'): void {
    this.trackEvent(GA_EVENTS.LOGIN, {
      method,
      event_category: 'authentication',
    });
  }

  /**
   * 用户注册事件
   */
  trackSignUp(method: string = 'email'): void {
    this.trackEvent(GA_EVENTS.SIGN_UP, {
      method,
      event_category: 'authentication',
    });
  }

  /**
   * 用户登出事件
   */
  trackLogout(): void {
    this.trackEvent(GA_EVENTS.LOGOUT, {
      event_category: 'authentication',
    });
  }

  /**
   * OAuth 登录尝试
   */
  trackOAuthLoginAttempt(provider: string): void {
    this.trackEvent(GA_EVENTS.OAUTH_LOGIN_ATTEMPT, {
      provider,
      event_category: 'oauth',
      event_label: `${provider}_login_attempt`,
    });
  }

  /**
   * OAuth 登录成功
   */
  trackOAuthLoginSuccess(provider: string): void {
    this.trackEvent(GA_EVENTS.OAUTH_LOGIN_SUCCESS, {
      provider,
      event_category: 'oauth',
      event_label: `${provider}_login_success`,
    });
  }

  /**
   * OAuth 登录错误
   */
  trackOAuthLoginError(provider: string, errorMessage: string): void {
    this.trackEvent(GA_EVENTS.OAUTH_LOGIN_ERROR, {
      provider,
      error_message: errorMessage,
      event_category: 'oauth',
      event_label: `${provider}_login_error`,
    });
  }

  /**
   * 查看定价页面
   */
  trackViewPricing(): void {
    this.trackEvent(GA_EVENTS.VIEW_PRICING, {
      event_category: 'ecommerce',
      event_label: 'pricing_page_view',
    });
  }

  /**
   * 开始结账流程
   */
  trackBeginCheckout(currency: string = 'USD', value?: number): void {
    this.trackEvent(GA_EVENTS.BEGIN_CHECKOUT, {
      currency,
      value,
      event_category: 'ecommerce',
    });
  }

  /**
   * 购买完成
   */
  trackPurchase(transactionId: string, value: number, currency: string = 'USD'): void {
    this.trackEvent(GA_EVENTS.PURCHASE, {
      transaction_id: transactionId,
      value,
      currency,
      event_category: 'ecommerce',
    });
  }

  /**
   * 订阅取消
   */
  trackSubscriptionCancel(reason?: string): void {
    this.trackEvent(GA_EVENTS.SUBSCRIPTION_CANCEL, {
      event_category: 'subscription',
      event_label: reason || 'user_initiated',
    });
  }

  /**
   * 查看功能页面
   */
  trackViewFeatures(): void {
    this.trackEvent(GA_EVENTS.VIEW_FEATURES, {
      event_category: 'engagement',
      event_label: 'features_page_view',
    });
  }

  /**
   * 查看个人中心
   */
  trackProfileView(): void {
    this.trackEvent(GA_EVENTS.PROFILE_VIEW, {
      event_category: 'engagement',
      event_label: 'profile_page_view',
    });
  }

  /**
   * 错误追踪
   */
  trackError(errorMessage: string, errorCategory: string = 'javascript'): void {
    this.trackEvent(GA_EVENTS.ERROR_OCCURRED, {
      error_message: errorMessage,
      event_category: 'error',
      event_label: errorCategory,
    });
  }

  /**
   * 设置用户属性
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.initialized) return;

    window.gtag('config', GA_MEASUREMENT_ID, {
      user_properties: properties,
    });
    
    log.debug('GA: 用户属性已设置', properties);
  }

  /**
   * 设置用户 ID（用于跨设备追踪）
   */
  setUserId(userId: string): void {
    if (!this.initialized) return;

    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId,
    });
    
    log.debug('GA: 用户ID已设置', { userId: userId.substring(0, 8) + '...' });
  }

  /**
   * 检查是否已初始化
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// 创建全局实例
export const analyticsService = new AnalyticsService();

// 导出便捷方法
export const analytics = {
  initialize: () => analyticsService.initialize(),
  trackPageView: (path?: string, title?: string) => analyticsService.trackPageView(path, title),
  trackEvent: (eventName: string, params?: GAEventParams) => analyticsService.trackEvent(eventName, params),
  trackLogin: (method?: string) => analyticsService.trackLogin(method),
  trackSignUp: (method?: string) => analyticsService.trackSignUp(method),
  trackLogout: () => analyticsService.trackLogout(),
  trackOAuthLoginAttempt: (provider: string) => analyticsService.trackOAuthLoginAttempt(provider),
  trackOAuthLoginSuccess: (provider: string) => analyticsService.trackOAuthLoginSuccess(provider),
  trackOAuthLoginError: (provider: string, error: string) => analyticsService.trackOAuthLoginError(provider, error),
  trackViewPricing: () => analyticsService.trackViewPricing(),
  trackBeginCheckout: (currency?: string, value?: number) => analyticsService.trackBeginCheckout(currency, value),
  trackPurchase: (transactionId: string, value: number, currency?: string) => analyticsService.trackPurchase(transactionId, value, currency),
  trackSubscriptionCancel: (reason?: string) => analyticsService.trackSubscriptionCancel(reason),
  trackViewFeatures: () => analyticsService.trackViewFeatures(),
  trackProfileView: () => analyticsService.trackProfileView(),
  trackError: (error: string, category?: string) => analyticsService.trackError(error, category),
  setUserProperties: (properties: Record<string, any>) => analyticsService.setUserProperties(properties),
  setUserId: (userId: string) => analyticsService.setUserId(userId),
  isInitialized: () => analyticsService.isInitialized(),
};

export default analyticsService;